// Export all Chakra UI components
export * from './badge';
export * from './button';
// RULE 15: Import and re-export Card to preserve full compound object structure
// Direct re-export loses Card.Title and Card.Description in Jest environment
import {
  Card as CardImport,
  CardRoot,
  CardHeader,
  CardBody,
  CardFooter,
  CardTitle,
  CardDescription,
  CardContent,
} from './card';
export const Card = CardImport;
export { CardRoot, CardHeader, CardBody, CardFooter, CardTitle, CardDescription, CardContent };
export type {
  CardProps,
  CardRootProps,
  CardHeaderProps,
  CardBodyProps,
  CardFooterProps,
  CardTitleProps,
  CardDescriptionProps,
  CardContentProps,
} from './card';
export * from './input';
export * from './input-number';
// Export NumberInput with dot notation support (NumberInput.Root, NumberInput.Field)
export { NumberInputComponent as NumberInput } from './number-input';
export {
  NumberInputRoot,
  NumberInputField,
  NumberInputControl,
  NumberInputIncrementTrigger,
  NumberInputDecrementTrigger,
} from './number-input';
export type { NumberInputProps } from './number-input';
export * from './checkbox';
export {
  Select,
  SelectRoot,
  SelectControl,
  SelectTrigger,
  SelectValueText,
  SelectIndicator,
  SelectIndicatorGroup,
  SelectClearTrigger,
  SelectPositioner,
  SelectContent,
  SelectItem,
  SelectItemText,
  SelectItemIndicator,
  SelectHiddenSelect,
  type SelectOption,
} from './select';
export { MultiSelect, type MultiSelectProps, type MultiSelectOption } from './multi-select';
export * from './date-picker';
export * from './date-range-picker';
export * from './radio';
export * from './switch';
export * from './data-table';
export * from './modal';
// Export legacy FormModal (deprecated - use EnhancedFormModal instead)
export { FormModal, type FormModalProps } from './legacy-form-modal';
// Export new enhanced form modal system
export * from './form-modal/';
export * from './dropdown';
export * from './tabs';
export * from './accordion';
export * from './sidebar';
export * from './breadcrumbs';
export * from './collapsible';

// Export core layout components
export * from './box';
export * from './text';
export * from './stack';
export * from './heading';
export * from './container';
export * from './flex';
export * from './spinner';
export * from './progress';
export * from './skeleton';
export * from './icon-button';
export * from './menu';
export * from './grid';
export * from './simple-grid';
export * from './separator';
export * from './native-select';
export * from './portal';
export * from './slider';
export * from './stat';
export * from './time-picker';
export * from './alert';
export * from './loading-state';

// Export navigation configuration
export * from '../navigation-config';

// Export directional icon components
export * from './directional-icon';

// Export Chakra UI v3 provider components
// Provider is exported from main provider.tsx, not from here to avoid conflicts
export { TmsThemeProvider } from './ui/theme-provider';
export { Toaster, createToaster, toaster } from './ui/toaster';
export { Tooltip } from './ui/tooltip';
export { Combobox, createListCollection } from './ui/combobox';
export type {
  ComboboxRootProps,
  ComboboxLabelProps,
  ComboboxControlProps,
  ComboboxInputProps,
  ComboboxTriggerProps,
  ComboboxIndicatorGroupProps,
  ComboboxClearTriggerProps,
  ComboboxPositionerProps,
  ComboboxContentProps,
  ComboboxEmptyProps,
  ComboboxItemProps,
  ComboboxItemGroupProps,
  ComboboxItemTextProps,
  ComboboxItemIndicatorProps,
} from './ui/combobox';

// Export unified Combobox component (custom implementation with search, virtual scrolling, RHF integration)
// Named exports to avoid conflict with Chakra UI Combobox
export {
  Combobox as UnifiedCombobox,
  ComboboxField,
  ComboboxTrigger as UnifiedComboboxTrigger,
  ComboboxContent as UnifiedComboboxContent,
  ComboboxSearch,
  ComboboxItem as UnifiedComboboxItem,
  ComboboxVirtualList,
  ComboboxMultiValue,
  ComboboxEmpty as UnifiedComboboxEmpty,
  ComboboxLoading,
  useComboboxSearch,
  useComboboxKeyboard,
  fuzzyMatch,
  flattenOptions,
  filterOptions,
} from './combobox';
export type {
  ComboboxOption,
  ComboboxOptionGroup,
  ComboboxProps as UnifiedComboboxProps,
  ComboboxFieldProps,
  ComboboxSearchConfig,
  ComboboxVirtualConfig,
  ComboboxAsyncConfig,
  ComboboxMultiConfig,
  ComboboxItemState,
} from './combobox';

// Export Chakra UI v3 utilities

// Re-export icon component from the original icon file
export { Icon } from './icon';

// Export enhanced form components with Chakra Field integration
export * from './form';

// Export React Hook Form integrated field components
export * from './form-fields';

// Export multi-step form components
export * from './multi-step-form';
export * from './step-navigator';
export * from './form-step';
export * from './multi-step-form-validation';

// Export dashboard components
export * from './dashboard';

// Export file management components
export * from './file-management';

// Export layout components
export * from './shell';
export * from './topbar';
export * from './page-layout';
export * from './empty-state';
export * from './loading-state';
export * from './layout-provider';

// Export advanced layout components
export * from './vertical-tabs';
export * from './icon-tabs';
export * from './accordion';
export { default as LegacyAccordion } from './accordion';
export * from './faq-accordion';
export * from './collapsible';
export * from './code';
export * from './drawer';

// Export error handling components
export * from './error-boundary';

// Export skeleton components
export * from './data-table-skeleton';

// Export CRUD components
export {
  ResourceForm,
  ResourceDetail,
  DeleteConfirmationDialog,
  type ResourceFormProps,
  type ResourceDetailProps,
  type DeleteConfirmationDialogProps,
  type DetailSection,
  type RelatedItem,
  type ResourceDetailTab,
  type FormSection,
  type FormFieldConfig,
} from './crud';

// Export timeline components
export * from './timeline';

// Export activity components
export * from './activity';

// Export avatar component
export * from './avatar';

// Export table components
export * from './table';

// Export popover component
export * from './popover';

// Export toast component
export * from './toast';

// Export collapse component
export * from './collapse';

// Export list components
export * from './list';

// Export divider component
export * from './divider';

// Export useToast hook
export { useToast } from '../hooks/useToast';

// Export textarea component
export * from './textarea';

// Export scroll area component
export * from './scroll-area';

// Export number input component
export * from './number-input';

// Export directional icon component
export * from './directional-icon';

// Export file upload component
export * from './file-upload';

// Export validation components
export * from './validation';

// Export complex form components
export * from './complex-forms';

// Export Chakra factory for creating styled native elements
// This enables patterns like chakra.button, chakra.div with style props
export { chakra } from '@chakra-ui/react';

// Re-export commonly used Radix UI icons
export {
  ArrowUpIcon,
  ArrowRightIcon,
  ArrowLeftIcon,
  ArrowDownIcon,
  CornerTopRightIcon,
  CornerTopLeftIcon,
  CircleIcon,
  CrossCircledIcon,
  RocketIcon,
  HomeIcon,
  CheckCircledIcon,
  ClockIcon,
  DotFilledIcon,
  CalendarIcon,
  InfoCircledIcon,
  ExclamationTriangleIcon,
  UpdateIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  DotsHorizontalIcon,
  Cross2Icon,
  MagnifyingGlassIcon,
  PersonIcon,
  FileTextIcon,
  MixerHorizontalIcon,
  UploadIcon,
  PlusIcon,
  TrashIcon,
  DownloadIcon,
  Share1Icon,
  CopyIcon,
  ScissorsIcon,
  MixIcon,
  ArchiveIcon,
  LockClosedIcon,
  LockOpen1Icon,
  ViewGridIcon,
  ListBulletIcon,
  ZoomInIcon,
  ZoomOutIcon,
  EnterFullScreenIcon,
  ExitIcon,
  Pencil1Icon,
  CursorArrowIcon,
  SquareIcon,
  ResetIcon,
  EyeOpenIcon,
  EyeNoneIcon,
  CheckIcon,
} from '@radix-ui/react-icons';
